/*
	ai_turn.ts
  Copyright (c) 2013-present Human Cube Inc.

  Generates a default turn for a player.
  Must all be synchronous and not involve any external DB or file access.  Also does not access
  any external user data.
  These restrictions are to allow it to be re-used in other environments such as the client.
  Currently bf_hitmap does not conform to these restrictions.

  TODO:  
      - In some places, assumes all cargo is a size of one, need to make it be able to handle other sizes.
      - Does not do SAME ZONE loading/unloading which will be needed for paratroops.
*/

import BF from '../bfcore/bfconst1';
import { Game, PlayerMoves, PlayerNormalTurn, PlayerPickTurn } from '../bfcore/gameTypes';
import { generatePickCapital, generatePickZone } from '../bfcore/bf_defaultpick';
import { WorkingData } from '../bfworking/workingTypes';
import { doTransportLoading, doTransportUnloading } from './ai_transports';
import { doPurchaseAndPlacement } from './ai_purchase';
import { doMoves } from './ai_move';
import { calcFoeDistances, calcFriendFoeCounts } from './ai_foe';


export function aiTurn (working: WorkingData, game: Game, allFlag: boolean): PlayerPickTurn | PlayerNormalTurn {
  // Generates a picking or normal movement (including purchaces) turn for a player.
  // CAREFUL NOT TO MODIFY VALUES IN game, AS IT WILL CORRUPT IT (It might be the source, not a copy).
  // working is cloned before calling so can be safely modified.
  // Set allFlag to true to generate all stages of the turn for the player, not just one.
  // Returns the generated turn, it can be a pick turn or a normal movement turn.

  const playerIndex = working.localPlayerIndex;

  if(game.turn < BF.TURN_PICK_CAPITAL) {
    return generatePickZone(game, playerIndex);
  }
  if(game.turn < BF.TURN_NORMAL) {
    return generatePickCapital(game, playerIndex);
  }

  let spendPercent;
  if(Array.isArray(game.autoSpend))
    spendPercent = game.autoSpend[playerIndex];
  if(!spendPercent || spendPercent < 0)
    spendPercent = 0;
  if(spendPercent > 100)
    spendPercent = 100;

  let movePercent;
  if(Array.isArray(game.autoMove))
    movePercent = game.autoMove[playerIndex];
  if(!movePercent || movePercent < 0)
    movePercent = 0;
  if(movePercent > 100)
    movePercent = 100;

  const moveStage = working.ai.moveStage;
  if (moveStage < 0) {
    // AI as done all the move stages, so do nothing.
    return {purchase: [], move: {}, land: {}, ai: working.ai};
  }

  const { friend, foe } = calcFriendFoeCounts(working, game);
  const { foeDistanceAir, foeDistanceLand } = calcFoeDistances(game, foe);
  working.ai.data = { foe, friend, foeDistanceAir, foeDistanceLand };

  // Only do purchase on the first stage of AI move stages:
  const purchase = (moveStage === 0) ? doPurchaseAndPlacement(working, game) : [];
  const move = allFlag ? doAllMoveStages(working, game) : doSingleMoveStage(working, game);
  const ai = { moveStage: working.ai.moveStage + 1, data: working.ai.data };
  const land = {};  // TODO: Do landings in the future!!!
  return {purchase, move, land, ai};
}

function doSingleMoveStage (working: WorkingData, game: Game): PlayerMoves {
  // CAREFUL NOT TO MODIFY VALUES IN game, AS IT WILL CORRUPT IT (It might be the source, not a copy).
  // working is cloned and can be modified if desired.

  // move: {
  //        forceID1: [
  //             zoneID to move to 1, x to move to 1, y to move to 1, extra 1,
  //             zoneID to move to 2, x to move to 2, y to move to 2, extra 2,
  //             zoneID to move to 3, x to move to 3, y to move to 3, extra 3],
  //        forceID2: [ 
  //             zoneID to move to 1, x to move to 1, y to move to 1, extra 1],
  //        forceID3: [
  //             zoneID to move to 1, x to move to 1, y to move to 1, extra 1,
  //             zoneID to move to 2, x to move to 2, y to move to 2, extra 2],
  //         ...
  //       },
  //   WHERE:  extra is 0 for normal movement
  //           extra is -1 for load cargo (zoneID is the force ID of cargo to load)
  //           extra is forceID of cargo to unload (zoneID, x, y is for destination of cargo)

  // Generates a single stage of movement for a player (specified by working.localPlayerIndex).
  // Returns an dictionary of force moves.
  const move: PlayerMoves = {};
  doTransportLoading(move, working, game);
  doMoves(move, working, game);
  doTransportUnloading(move, working, game);
  return move;
}

function doAllMoveStages (working: WorkingData, game: Game): PlayerMoves {
  // CAREFUL NOT TO MODIFY VALUES IN game, AS IT WILL CORRUPT IT (It might be the source, not a copy).
  // working is cloned and can be modified if desired.
  // Generates all stages of movement for a player (specified by working.localPlayerIndex).
  // Returns an dictionary of force moves.
  const move: PlayerMoves = {};

  if(working.ai.moveStage < 0) {
    // Already done all move stages.
    return move;
  }

  for(; working.ai.moveStage < BF.FORCE_MAX_MOVES; working.ai.moveStage++) {
    doTransportLoading(move, working, game);
    doMoves(move, working, game);
    doTransportUnloading(move, working, game);
  }

  return move;
}
